import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A Console file that is not a picture opens here, on the same page (Kyu,
 * 12 Oct): a PDF in a full-screen sheet, any other file as a name and a Save
 * button. Same chrome as the image viewer (ImageLightbox) and the same
 * promises: it never opens a tab, the route never changes, and the page
 * underneath is never remounted, so a voice note being recorded is untouched.
 * The file always comes through the token link (huddleFileUrl), never Drive.
 *
 * Closing goes through history.back() so the Back button and the Close button
 * are one path: opening adds one history entry with the same URL.
 */

export interface SheetFile {
  name: string;
  url: string;
  mime: string | null;
}

export function ConsoleFileSheet({ file, onClose }: { file: SheetFile; onClose: () => void }) {
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const isPdf = file.mime === 'application/pdf';

  const close = useCallback(() => window.history.back(), []);

  // The latest onClose, read by the listener, so a new function from the
  // parent never re-runs the effect (which would push another history entry).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onPop = () => onCloseRef.current();
    window.history.pushState({ ...(window.history.state as object | null), fileSheet: true }, '');
    window.addEventListener('popstate', onPop);
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') window.history.back();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  /**
   * The file is served from another origin, where a plain download attribute
   * is ignored and the browser would navigate away from the page. Fetching to
   * a blob keeps Alex on the page and gives iPhone Safari its save sheet. When
   * the fetch fails we say so; we never open a tab.
   */
  async function save() {
    setSaving(true);
    setFailed(false);
    try {
      const response = await fetch(file.url, { mode: 'cors' });
      if (!response.ok) throw new Error(String(response.status));
      const href = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = href;
      link.download = file.name;
      link.click();
      URL.revokeObjectURL(href);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div className="lightbox lightbox--file" role="dialog" aria-modal="true" aria-label={file.name}>
      <div className="lightbox-bar">
        <span className="lightbox-file-name">{file.name}</span>
        <a
          className="lightbox-action"
          href={file.url}
          download={file.name}
          onClick={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </a>
        <button type="button" className="lightbox-action" ref={closeRef} onClick={close}>
          Close
        </button>
      </div>
      {isPdf ? (
        <div className="lightbox-file-body">
          <iframe className="lightbox-file-frame" src={file.url} title={file.name} />
        </div>
      ) : (
        <div className="lightbox-file-body lightbox-file-body--plain">
          <p>{file.name}</p>
          <p>This file cannot be shown here. Tap Save to open it.</p>
        </div>
      )}
      <p className="lightbox-file-note" role={failed ? 'alert' : undefined}>
        {failed
          ? 'The file did not save. Check your connection and tap Save again.'
          : isPdf
            ? 'Not showing? Tap Save to open it.'
            : ''}
      </p>
    </div>,
    document.body,
  );
}
